import { useMemo } from 'react';

import {
  MultiQuestion,
  Option,
  Question,
  Step,
  InfoBanner,
} from '@de/types/types';
import { StepType } from '@de/types/enums';
import type { StepData } from '@de/providers/dynamicExperienceReducer';
import { filterAndPrioritizeInfoBanners } from '@de/utils/filterInfoBanners';

export interface UseInfoBannersArgs {
  currentStep?: Step;
  currentStepData?:
    | (Partial<StepData> & Record<string, unknown>)
    | null
    | undefined;
  allSelectedOptions: Option[];
}

export const useInfoBanners = ({
  currentStep,
  currentStepData,
  allSelectedOptions,
}: UseInfoBannersArgs) => {
  const visibleInfoBanners = useMemo(() => {
    if (!currentStep) return [];

    const isQuestionStep =
      currentStep.type === StepType.QUESTION ||
      currentStep.type === StepType.MULTI_QUESTION;

    if (!isQuestionStep) return [];

    // Collect InfoBanners only from question-level configs
    const allInfoBannerConfigs: InfoBanner[] = [];

    if (currentStep.type === StepType.MULTI_QUESTION) {
      const multiQuestion = currentStep as MultiQuestion;

      // Add InfoBanners from individual questions within the multi-question step
      if (multiQuestion.stepQuestions) {
        multiQuestion.stepQuestions.forEach((question) => {
          if (question.infoBannerConfigs) {
            question.infoBannerConfigs.forEach((config) => {
              allInfoBannerConfigs.push(config);
            });
          }
        });
      }
    } else {
      // Regular QUESTION step
      const question = currentStep as Question;
      if (question.infoBannerConfigs) {
        question.infoBannerConfigs.forEach((config) => {
          allInfoBannerConfigs.push(config);
        });
      }
    }

    // Early return if no InfoBanners
    if (allInfoBannerConfigs.length === 0) return [];

    // Filter options to only include those relevant to current step
    const relevantOptions = allSelectedOptions.filter((option) => {
      // For MULTI_QUESTION, check if option belongs to any embedded question
      if (currentStep.type === StepType.MULTI_QUESTION) {
        const multiQuestion = currentStep as MultiQuestion;

        // Check in stepQuestions array (if exists)
        // MULTI_QUESTION is a container - only include options from its embedded questions
        if (multiQuestion.stepQuestions) {
          return multiQuestion.stepQuestions.some((question) =>
            question.options?.some(
              (qOption) => qOption.optionId === option.optionId,
            ),
          );
        }
      }
      // For regular QUESTION, check if option belongs to this question
      else if (currentStep.type === StepType.QUESTION) {
        const question = currentStep as Question;

        // Check direct option mapping first
        const belongsToQuestion = question.options?.some(
          (qOption) => qOption.optionId === option.optionId,
        );

        if (belongsToQuestion) return true;

        // Also check leadFieldAssociated for dynamic behavior
        // Only match if option's field association matches this question's lead field association
        if (
          option.leadFieldAssociated &&
          option.leadFieldAssociated === question.leadFieldAssociated
        ) {
          return true;
        }
      }
      return false;
    });

    // Use shared utility for filtering and prioritizing banners
    return filterAndPrioritizeInfoBanners({
      configs: allInfoBannerConfigs,
      selectedOptions: relevantOptions,
      stepData: currentStepData,
    });
  }, [currentStep, currentStepData, allSelectedOptions]);

  return { visibleInfoBanners };
};
